import { useCallback, useEffect, useState } from "react";
import api from "@/api/client";
import { useAuthStore } from "@/store/authStore";

/* ════════════════════════════════════════════════════════════════════════════
   Lernwelten progress + XP
   ----------------------------------------------------------------------------
   Per-user progress lives in localStorage (fast, offline-friendly, no new
   kids'-data surface). XP/coins are awarded for REAL through the existing
   server-validated endpoint POST /students/me/minigame-reward, which caps the
   reward server-side (≤200 coins / ≤400 XP) — so progress can never be forged
   into unlimited rewards. The store's coin/XP counter updates instantly for feel.
   ════════════════════════════════════════════════════════════════════════════ */

const KEY = (uid) => `studox-flow-learn-v1:${uid || "anon"}`;

function todayStr() { return new Date().toISOString().slice(0, 10); }
function isYesterday(day) {
  const d = new Date(); d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10) === day;
}

function emptyState() {
  return { completed: {}, streak: { count: 0, lastDay: null }, totalXp: 0, totalStars: 0, shieldUsed: false };
}

function read(uid) {
  try { const raw = localStorage.getItem(KEY(uid)); return raw ? { ...emptyState(), ...JSON.parse(raw) } : emptyState(); }
  catch { return emptyState(); }
}
function write(uid, state) { try { localStorage.setItem(KEY(uid), JSON.stringify(state)); } catch {} }

/** stars 0–3 from accuracy. 100% → 3, ≥75% → 2, ≥50% → 1, else 0. */
export function starsFor(correct, total) {
  if (!total) return 0;
  const r = correct / total;
  return r >= 1 ? 3 : r >= 0.75 ? 2 : r >= 0.5 ? 1 : 0;
}

export function useLearnProgress() {
  const uid = useAuthStore((s) => s.user?.id);
  const [state, setState] = useState(() => read(uid));
  // Belohnungszentrum perks (owned via the Shop) — honest, small mechanics:
  //  · xpBoost:      permanent +10% XP on every module (si-rz-xpboost)
  //  · streakShield: forgives exactly ONE missed day, once (si-rz-streakshield)
  const [perks, setPerks] = useState({ xpBoost: false, streakShield: false });

  // re-read when the user changes
  useEffect(() => { setState(read(uid)); }, [uid]);

  useEffect(() => {
    let alive = true;
    api.get("/shop/inventory").then(({ data }) => {
      if (!alive) return;
      const owned = new Set((data?.inventory || []).map((i) => i.itemId));
      setPerks({ xpBoost: owned.has("si-rz-xpboost"), streakShield: owned.has("si-rz-streakshield") });
    }).catch(() => {}); // not a student yet / offline → no perks, harmless
    return () => { alive = false; };
  }, [uid]);

  const refresh = useCallback(() => setState(read(uid)), [uid]);

  /** Save a finished module: stars, streak, and award (capped) XP/coins server-side. */
  const recordResult = useCallback(async ({ moduleId, correct, total, xp = 0, coins = 0 }) => {
    const stars = starsFor(correct, total);
    const cur = read(uid);
    const prev = cur.completed[moduleId];
    const firstClear = !prev;
    const improved = !prev || stars > prev.stars;

    // streak: bump once per calendar day. The Streak-Schild perk forgives exactly
    // one missed day, one time ever (matches its "einmalig" shop description).
    const day = todayStr();
    let streak = cur.streak || { count: 0, lastDay: null };
    let shieldUsed = cur.shieldUsed || false;
    if (streak.lastDay !== day) {
      const missedADay = streak.lastDay && !isYesterday(streak.lastDay);
      if (missedADay && perks.streakShield && !shieldUsed) {
        streak = { count: streak.count + 1, lastDay: day };
        shieldUsed = true;
      } else {
        streak = { count: isYesterday(streak.lastDay) ? streak.count + 1 : 1, lastDay: day };
      }
    }

    // XP-Boost perk: honest +10%; still passes through the server's hard XP cap.
    const boostedXp = perks.xpBoost ? Math.round(xp * 1.1) : xp;
    // Only pay XP for a first clear or an improvement (prevents grind-farming a cleared module)
    const payXp = improved ? boostedXp : Math.round(boostedXp * 0.15);
    const payCoins = improved ? coins : Math.round(coins * 0.15);

    const next = {
      ...cur,
      completed: { ...cur.completed, [moduleId]: { stars: Math.max(stars, prev?.stars || 0), best: Math.max(correct, prev?.best || 0), ts: Date.now() } },
      streak,
      shieldUsed,
      totalXp: (cur.totalXp || 0) + payXp,
      totalStars: Object.values({ ...cur.completed, [moduleId]: { stars: Math.max(stars, prev?.stars || 0) } }).reduce((a, c) => a + (c.stars || 0), 0),
    };
    write(uid, next);
    setState(next);

    // Award real, server-capped XP/coins + instant store update for the TopBar.
    let awarded = { xp: 0, coins: 0 };
    if (payXp > 0 || payCoins > 0) {
      try {
        const { data } = await api.post("/students/me/minigame-reward", { xp: payXp, coins: payCoins });
        awarded = { xp: data?.xp || 0, coins: data?.coins || 0 };
        if (awarded.xp || awarded.coins) {
          const sp = useAuthStore.getState().user?.studentProfile;
          if (sp) useAuthStore.getState().updateStudentStats({ xp: (sp.xp || 0) + awarded.xp, coins: (sp.coins || 0) + awarded.coins });
        }
      } catch { /* offline / not a student → progress still saved locally, no reward */ }
    }
    return { stars, firstClear, improved, awarded, streak: streak.count };
  }, [uid, perks]);

  return {
    perks,
    completed: state.completed,
    streak: state.streak?.count || 0,
    totalStars: state.totalStars || 0,
    isDone: (id) => !!state.completed[id],
    starsOf: (id) => state.completed[id]?.stars || 0,
    recordResult,
    refresh,
  };
}
